const RANGE_FUNCS = new Set([
  "rate", "irate", "increase", "delta", "idelta", "changes", "deriv", "resets",
  "avg_over_time", "sum_over_time", "min_over_time", "max_over_time",
  "count_over_time", "quantile_over_time", "stddev_over_time", "stdvar_over_time",
  "last_over_time", "present_over_time", "absent_over_time",
]);

const BINARY_OPS = ["==", "!=", ">=", "<=", ">", "<"];

function stripOuterParens(expr) {
  let trimmed = expr.trim();
  while (trimmed.startsWith("(") && trimmed.endsWith(")")) {
    const closeIdx = findMatchingParen(trimmed, 0);
    if (closeIdx !== trimmed.length - 1) break;
    trimmed = trimmed.slice(1, -1).trim();
  }
  return trimmed;
}

function peelBinaryOperator(expr) {
  const trimmed = expr.trim();
  if (!trimmed) return null;

  let depth = 0;
  let inString = false;
  let escape = false;

  for (let i = trimmed.length - 1; i >= 0; i -= 1) {
    const ch = trimmed[i];

    if (escape) {
      escape = false;
      continue;
    }
    if (ch === "\\" && inString) {
      escape = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;

    if (ch === ")") depth += 1;
    else if (ch === "(") depth -= 1;
    else if (depth === 0) {
      for (const op of BINARY_OPS) {
        const opStart = i - op.length + 1;
        if (opStart < 0) continue;
        if (trimmed.slice(opStart, i + 1) !== op) continue;

        const left = trimmed.slice(0, opStart).trim();
        const right = trimmed.slice(i + 1).trim();
        if (!left || !right) continue;

        return {
          op: { id: op, display: op, params: { value: right } },
          inner: stripOuterParens(left),
        };
      }
    }
  }
  return null;
}

function findMatchingParen(str, openIdx) {
  let depth = 0;
  for (let i = openIdx; i < str.length; i += 1) {
    const ch = str[i];
    if (ch === "(") depth += 1;
    else if (ch === ")") {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function splitAtDepth0(input) {
  const parts = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < input.length; i += 1) {
    const ch = input[i];
    if (ch === "(") depth += 1;
    else if (ch === ")") depth -= 1;
    else if (ch === "," && depth === 0) {
      parts.push(input.slice(start, i).trim());
      start = i + 1;
    }
  }
  parts.push(input.slice(start).trim());
  return parts;
}

function parseLabelPairs(inner) {
  const pairs = [];
  let pos = 0;
  while (pos < inner.length) {
    const keyMatch = inner.slice(pos).match(
      /^\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*(=|!=|=~|!~)\s*"((?:[^"\\]|\\.)*)"\s*/
    );
    if (!keyMatch) break;
    pairs.push({
      key: keyMatch[1],
      op: keyMatch[2],
      value: keyMatch[3].replace(/\\(.)/g, "$1"),
    });
    pos += keyMatch[0].length;
    if (inner.slice(pos).trimStart().startsWith(",")) {
      pos = inner.indexOf(",", pos) + 1;
    } else {
      break;
    }
  }
  return pairs;
}

function parseLabelSelector(expr) {
  const trimmed = expr.trim();
  const withMetric = trimmed.match(/^([a-zA-Z_:][a-zA-Z0-9_:]*)\{([^}]+)\}$/);
  if (withMetric) {
    const pairs = parseLabelPairs(withMetric[2].trim());
    return {
      metric: withMetric[1],
      labelFilters: pairs.length
        ? pairs.map((p) => ({ key: p.key, op: p.op, value: p.value }))
        : [{ key: "", op: "=", value: "" }],
    };
  }

  if (/^[a-zA-Z_:][a-zA-Z0-9_:]*$/.test(trimmed)) {
    return { metric: trimmed, labelFilters: [{ key: "", op: "=", value: "" }] };
  }

  const braceOnly = trimmed.match(/^\{([^}]+)\}$/);
  if (braceOnly) {
    const pairs = parseLabelPairs(braceOnly[1].trim());
    return {
      metric: "",
      labelFilters: pairs.length
        ? pairs.map((p) => ({ key: p.key, op: p.op, value: p.value }))
        : [{ key: "", op: "=", value: "" }],
    };
  }

  return { metric: "", labelFilters: [{ key: "", op: "=", value: "" }] };
}

function peelOneOperation(expr) {
  const trimmed = expr.trim();
  if (!trimmed) return null;

  const byPrefixMatch = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s+by\s*\(/);
  if (byPrefixMatch) {
    const opId = byPrefixMatch[1];
    const byOpen = trimmed.indexOf("(", byPrefixMatch.index + byPrefixMatch[0].length - 1);
    const byClose = findMatchingParen(trimmed, byOpen);
    if (byClose === -1) return null;

    const byLabels = trimmed
      .slice(byOpen + 1, byClose)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const rest = trimmed.slice(byClose + 1).trim();
    if (!rest.startsWith("(")) return null;

    const innerClose = findMatchingParen(rest, 0);
    if (innerClose === -1) return null;

    return {
      op: { id: opId, display: opId, params: { by: byLabels } },
      inner: rest.slice(1, innerClose).trim(),
    };
  }

  const funcMatch = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\(/);
  if (!funcMatch) return null;

  const opId = funcMatch[1];
  const openIdx = funcMatch[0].length - 1;
  const closeIdx = findMatchingParen(trimmed, openIdx);
  if (closeIdx === -1) return null;

  const argsContent = trimmed.slice(openIdx + 1, closeIdx).trim();
  let trailingBy = null;
  const afterClose = trimmed.slice(closeIdx + 1).trim();
  if (afterClose.startsWith("by")) {
    const byOpen = afterClose.indexOf("(");
    const byClose = findMatchingParen(afterClose, byOpen);
    if (byClose !== -1) {
      trailingBy = afterClose
        .slice(byOpen + 1, byClose)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }

  const withBy = (params) => ({
    op: {
      id: opId,
      display: opId,
      params: trailingBy?.length ? { ...params, by: trailingBy } : params,
    },
    inner: null,
  });

  if (RANGE_FUNCS.has(opId)) {
    if (opId === "quantile_over_time") {
      const parts = splitAtDepth0(argsContent);
      if (parts.length >= 2) {
        const rangeMatch = parts[1].match(/^(.+)\[([^\]]+)\]$/s);
        if (rangeMatch) {
          return {
            op: {
              id: opId,
              display: opId,
              params: { q: parseFloat(parts[0]), range: rangeMatch[2].trim() },
            },
            inner: rangeMatch[1].trim(),
          };
        }
      }
    }

    const rangeMatch = argsContent.match(/^(.+)\[([^\]]+)\]$/s);
    if (rangeMatch) {
      return {
        op: { id: opId, display: opId, params: { range: rangeMatch[2].trim() } },
        inner: rangeMatch[1].trim(),
      };
    }
  }

  if (opId === "predict_linear") {
    const parts = splitAtDepth0(argsContent);
    if (parts.length >= 2) {
      const rangeMatch = parts[0].match(/^(.+)\[([^\]]+)\]$/s);
      if (rangeMatch) {
        return {
          op: {
            id: opId,
            display: opId,
            params: { range: rangeMatch[2].trim(), t: parseFloat(parts[1]) },
          },
          inner: rangeMatch[1].trim(),
        };
      }
    }
  }

  if (opId === "topk" || opId === "bottomk") {
    const parts = splitAtDepth0(argsContent);
    if (parts.length >= 2) {
      const peeled = withBy({ k: parseFloat(parts[0]) });
      peeled.inner = parts.slice(1).join(",").trim();
      return peeled;
    }
  }

  if (opId === "quantile" || opId === "histogram_quantile") {
    const parts = splitAtDepth0(argsContent);
    if (parts.length >= 2) {
      const peeled = withBy({ q: parseFloat(parts[0]) });
      peeled.inner = parts.slice(1).join(",").trim();
      return peeled;
    }
  }

  if (opId === "count_values") {
    const labelMatch = argsContent.match(/^"((?:[^"\\]|\\.)*)"\s*,\s*(.+)$/s);
    if (labelMatch) {
      const peeled = withBy({ label: labelMatch[1] });
      peeled.inner = labelMatch[2].trim();
      return peeled;
    }
  }

  if (opId === "clamp") {
    const parts = splitAtDepth0(argsContent);
    if (parts.length >= 3) {
      return {
        op: {
          id: opId,
          display: opId,
          params: { min: parseFloat(parts[1]), max: parseFloat(parts[2]) },
        },
        inner: parts[0],
      };
    }
  }

  if (opId === "clamp_min" || opId === "clamp_max") {
    const parts = splitAtDepth0(argsContent);
    if (parts.length >= 2) {
      return {
        op: {
          id: opId,
          display: opId,
          params: opId === "clamp_min" ? { min: parseFloat(parts[1]) } : { max: parseFloat(parts[1]) },
        },
        inner: parts[0],
      };
    }
  }

  if (opId === "time" || opId === "pi") {
    return null;
  }

  const peeled = withBy({});
  peeled.inner = argsContent;
  return peeled;
}

function peelOneLayer(expr) {
  return peelOneOperation(expr) || peelBinaryOperator(expr);
}

export function parsePromqlExpression(expression) {
  if (!expression || typeof expression !== "string") {
    return { metric: "", labelFilters: [{ key: "", op: "=", value: "" }], ops: [] };
  }

  let current = expression.trim();
  const ops = [];

  while (current) {
    const peeled = peelOneLayer(current);
    if (!peeled?.inner) break;
    ops.unshift(peeled.op);
    current = peeled.inner.trim();
  }

  const { metric, labelFilters } = parseLabelSelector(current);
  return { metric, labelFilters, ops };
}

export default parsePromqlExpression;
